import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  AssignmentLate as LateIcon,
  Gavel as GavelIcon,
  Flag as FlagIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatScheduledDate } from '../../utils/dateUtils';
import SignaturePad from './Forms/SignaturePad';

// TKT-462: the manager and (if the employee refuses) a witness sign the
// warning too, as on Paramount's paper Employee Warning Notice.
const SIGNERS = ['employee', 'manager', 'witness'];

const EmployeeDiscipline = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Save errors show inside the write-up dialog; the page-level alert sits behind it.
  const [warningFormError, setWarningFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('list'); // 'list', 'analytics', 'points'
  
  // Data states
  const [warnings, setWarnings] = useState([]);
  const [offenseTypes, setOffenseTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({});
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingWarning, setEditingWarning] = useState(null);
  const [selectedWarning, setSelectedWarning] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    employee_id: '',
    offense_type_id: '',
    warning_date: '',
    type_of_warning: '',
    description: '',
    points_assessed: 0,
    status: 'active',
    expiration_date: '',
    follow_up_date: '',
    improvement_plan: '',
    consequence_of_infraction: '',
    employee_signature: null,
    employee_sign_date: '',
    manager_id: '',
    manager_signature: null,
    manager_sign_date: '',
    witness_signature: null,
    witness_sign_date: ''
  });

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Points system state (FB-065)
  const [pointsData, setPointsData] = useState([]);
  const [pointsSummary, setPointsSummary] = useState({});
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsEmployeeFilter, setPointsEmployeeFilter] = useState('all');

  // TKT-269: the Points System tab was read-only - the backend has always
  // accepted point entries, but nothing in the UI could create one, so the
  // history stayed permanently empty and the totals stayed at zero.
  const [pointsDialogOpen, setPointsDialogOpen] = useState(false);
  const [pointsSaving, setPointsSaving] = useState(false);
  const [pointsFormError, setPointsFormError] = useState('');
  const emptyPointsForm = {
    employee_id: '',
    entry_type: 'awarded',
    points: '',
    description: ''
  };
  const [pointsForm, setPointsForm] = useState(emptyPointsForm);

  // Analytics state (FB-065)
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    employee: 'all',
    status: 'all',
    offense_type: 'all',
    severity: 'all'
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadWarnings(),
        loadOffenseTypes(),
        loadEmployees(),
        loadStats()
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadWarnings = async () => {
    try {
      const response = await makeApiCall('api/discipline/warnings');
      if (response.ok) {
        const data = await response.json();
        setWarnings(data.warnings || []);
      } else {
        setWarnings(getMockWarnings());
      }
    } catch (err) {
      console.error('Error loading warnings:', err);
      setWarnings(getMockWarnings());
    }
  };

  const loadOffenseTypes = async () => {
    try {
      const response = await makeApiCall('api/discipline/offense-types');
      if (response.ok) {
        const data = await response.json();
        setOffenseTypes(data.offense_types || []);
      } else {
        setOffenseTypes(getMockOffenseTypes());
      }
    } catch (err) {
      console.error('Error loading offense types:', err);
      setOffenseTypes(getMockOffenseTypes());
    }
  };

  // TKT-268: /api/employees paginates at 10 by default, so the employee
  // dropdowns here only ever offered the first ten names alphabetically -
  // anyone later in the alphabet (Doug Dolson, Naomi Wallace) simply wasn't
  // in the list. Ask for the full roster the way the other pickers do.
  const loadEmployees = async () => {
    try {
      const response = await makeApiCall('api/employees?page=1&limit=1000');
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await makeApiCall('api/discipline/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || {});
      } else {
        setStats(getMockStats());
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      setStats(getMockStats());
    }
  };

  const getMockOffenseTypes = () => [
    { id: 1, offense_name: 'Tardiness/Leaving Early', description: 'Arriving late or leaving early without permission', severity_level: 1 },
    { id: 2, offense_name: 'Absenteeism', description: 'Frequent or unexcused absences', severity_level: 2 },
    { id: 3, offense_name: 'Violation of Company Policies', description: 'Failure to follow established company policies', severity_level: 2 },
    { id: 4, offense_name: 'Substandard Work', description: 'Work quality below expected standards', severity_level: 2 },
    { id: 5, offense_name: 'Violation of Safety Rules', description: 'Failure to follow safety protocols', severity_level: 3 },
    { id: 6, offense_name: 'Rudeness to Customers/Coworkers', description: 'Inappropriate behavior towards customers or colleagues', severity_level: 3 }
  ];

  const getMockWarnings = () => [
    {
      id: 1,
      employee_id: 1,
      employee_name: 'John Doe',
      offense_type_id: 1,
      offense_name: 'Tardiness/Leaving Early',
      warning_date: '2025-10-15',
      type_of_warning: 'Verbal Warning',
      description: 'Arrived 30 minutes late without prior notice',
      points_assessed: 1,
      status: 'active',
      expiration_date: '2026-01-15',
      follow_up_date: '2025-11-15',
      created_at: '2025-10-15T10:00:00Z'
    },
    {
      id: 2,
      employee_id: 2,
      employee_name: 'Jane Smith',
      offense_type_id: 2,
      offense_name: 'Absenteeism',
      warning_date: '2025-10-10',
      type_of_warning: 'Written Warning',
      description: 'Three unexcused absences in the past month',
      points_assessed: 2,
      status: 'active',
      expiration_date: '2026-01-10',
      follow_up_date: '2025-11-10',
      created_at: '2025-10-10T14:30:00Z'
    },
    {
      id: 3,
      employee_id: 3,
      employee_name: 'Mike Johnson',
      offense_type_id: 5,
      offense_name: 'Violation of Safety Rules',
      warning_date: '2025-10-05',
      type_of_warning: 'Final Warning',
      description: 'Failed to wear required safety equipment',
      points_assessed: 3,
      status: 'active',
      expiration_date: '2026-01-05',
      follow_up_date: '2025-11-05',
      created_at: '2025-10-05T09:15:00Z'
    },
    {
      id: 4,
      employee_id: 1,
      employee_name: 'John Doe',
      offense_type_id: 1,
      offense_name: 'Tardiness/Leaving Early',
      warning_date: '2025-09-20',
      type_of_warning: 'Verbal Warning',
      description: 'Left work 15 minutes early without permission',
      points_assessed: 1,
      status: 'resolved',
      expiration_date: '2025-12-20',
      follow_up_date: '2025-10-20',
      created_at: '2025-09-20T16:45:00Z'
    }
  ];

  const getMockStats = () => ({
    total_warnings: 12,
    active_warnings: 8,
    resolved_warnings: 4,
    total_points: 25,
    employees_with_warnings: 5,
    most_common_offense: 'Tardiness/Leaving Early',
    average_points_per_employee: 5.0
  });

  const getSeverityColor = (level) => {
    switch (level) {
      case 1: return 'success';
      case 2: return 'warning';
      case 3: return 'error';
      default: return 'default';
    }
  };

  const getSeverityIcon = (level) => {
    switch (level) {
      case 1: return <FlagIcon />;
      case 2: return <WarningIcon />;
      case 3: return <BlockIcon />;
      default: return <WarningIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'error';
      case 'resolved': return 'success';
      case 'expired': return 'default';
      default: return 'default';
    }
  };

  const getWarningTypeColor = (type) => {
    switch (type) {
      case 'Verbal Warning': return 'info';
      case 'Written Warning': return 'warning';
      case 'Final Warning': return 'error';
      default: return 'default';
    }
  };

  // Convert warning type string to numeric value
  const convertWarningTypeToNumber = (typeString) => {
    const typeMap = {
      'Verbal Warning': 1,
      'Written Warning': 2,
      'Final Warning': 3
    };
    return typeMap[typeString] || null;
  };

  // Convert warning type number to string
  const convertWarningTypeToString = (typeNumber) => {
    const typeMap = {
      1: 'Verbal Warning',
      2: 'Written Warning',
      3: 'Final Warning'
    };
    return typeMap[typeNumber] || '';
  };

  const handleCreateWarning = () => {
    setEditingWarning(null);
    setFormData({
      employee_id: '',
      offense_type_id: '',
      warning_date: new Date().toISOString().split('T')[0],
      type_of_warning: '',
      description: '',
      points_assessed: 0,
      status: 'active',
      expiration_date: '',
      follow_up_date: '',
      improvement_plan: '',
      consequence_of_infraction: '',
      employee_signature: null,
      employee_sign_date: '',
      manager_id: '',
      manager_signature: null,
      manager_sign_date: '',
      witness_signature: null,
      witness_sign_date: ''
    });
    setWarningFormError('');
    setOpenDialog(true);
  };

  // Helper function to format date for input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Extract just the date part if it's a timestamp
    const date = dateString.split('T')[0];
    return date;
  };

  const handleEditWarning = (warning) => {
    setEditingWarning(warning);
    setFormData({
      employee_id: warning.employee_id,
      offense_type_id: warning.offense_type_id || '',
      warning_date: formatDateForInput(warning.warning_date),
      type_of_warning: convertWarningTypeToString(warning.type_of_warning) || warning.type_of_warning,
      description: warning.description || warning.description_of_infraction || '',
      points_assessed: warning.points_assessed || 0,
      status: warning.status || 'active',
      expiration_date: formatDateForInput(warning.expiration_date),
      follow_up_date: formatDateForInput(warning.follow_up_date),
      improvement_plan: warning.improvement_plan || '',
      consequence_of_infraction: warning.consequence_of_infraction || '',
      employee_signature: warning.employee_signature || null,
      employee_sign_date: formatDateForInput(warning.employee_sign_date),
      manager_id: warning.manager_id || '',
      manager_signature: warning.manager_signature || null,
      manager_sign_date: formatDateForInput(warning.manager_sign_date),
      witness_signature: warning.witness_signature || null,
      witness_sign_date: formatDateForInput(warning.witness_sign_date)
    });
    setWarningFormError('');
    setOpenDialog(true);
  };

  const handleViewWarning = (warning) => {
    setSelectedWarning(warning);
  };

  // Load points data for Points System view (FB-065)
  const loadPointsForEmployee = async (employeeId) => {
    if (!employeeId || employeeId === 'all') {
      setPointsData([]);
      setPointsSummary({});
      return;
    }
    setPointsLoading(true);
    try {
      const response = await makeApiCall(`api/discipline/points/history/${employeeId}?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setPointsData(data.points_history || []);
        setPointsSummary(data.summary || {});
      } else {
        setPointsData([]);
        setPointsSummary({});
      }
    } catch (err) {
      console.error('Error loading points history:', err);
      setPointsData([]);
      setPointsSummary({});
    } finally {
      setPointsLoading(false);
    }
  };

  // TKT-269: record a points entry against an employee.
  const handleOpenPointsDialog = () => {
    setPointsFormError('');
    setPointsForm({
      ...emptyPointsForm,
      // Pre-select whoever is already being viewed so the common case is one click.
      employee_id: pointsEmployeeFilter !== 'all' ? pointsEmployeeFilter : ''
    });
    setPointsDialogOpen(true);
  };

  const handleSavePoints = async () => {
    const amount = Number(pointsForm.points);
    if (!pointsForm.employee_id) {
      setPointsFormError('Select an employee.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setPointsFormError('Enter a number of points greater than zero.');
      return;
    }

    setPointsSaving(true);
    setPointsFormError('');
    try {
      const awarded = pointsForm.entry_type === 'awarded';
      const response = await makeApiCall('api/discipline/points', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: pointsForm.employee_id,
          points_awarded: awarded ? amount : 0,
          points_deducted: awarded ? 0 : amount,
          description_of_conduct: awarded ? pointsForm.description : null,
          description_of_misconduct: awarded ? null : pointsForm.description
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to record points');
      }

      setPointsDialogOpen(false);
      setPointsForm(emptyPointsForm);
      // Surfaced on the page rather than inside the dialog, so the confirmation
      // isn't hidden behind the modal (TKT-271).
      setSuccess(`Points ${awarded ? 'awarded' : 'deducted'} successfully`);

      if (pointsEmployeeFilter === pointsForm.employee_id) {
        loadPointsForEmployee(pointsForm.employee_id);
      } else {
        // Switching the filter reloads the history via the view effect.
        setPointsEmployeeFilter(pointsForm.employee_id);
      }
    } catch (err) {
      console.error('Error recording points:', err);
      setPointsFormError(err.message || 'Failed to record points');
    } finally {
      setPointsSaving(false);
    }
  };

  // Load analytics data for Analytics view (FB-065)
  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await makeApiCall('api/discipline/reports?report_type=summary');
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        setAnalyticsData(null);
      }
    } catch (err) {
      console.error('Error loading discipline analytics:', err);
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'points') {
      loadPointsForEmployee(pointsEmployeeFilter);
    } else if (view === 'analytics' && !analyticsData) {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, pointsEmployeeFilter]);
  
  const handleCloseViewDialog = () => {
    setSelectedWarning(null);
  };

  const handleSaveWarning = async () => {
    setWarningFormError('');
    try {
      const url = editingWarning ? `api/discipline/warnings/${editingWarning.id}` : 'api/discipline/warnings';
      const method = editingWarning ? 'PUT' : 'POST';
      
      const apiData = {
        ...formData,
        type_of_warning: convertWarningTypeToNumber(formData.type_of_warning) || formData.type_of_warning,
        employee_sign_date: formData.employee_signature ? (formData.employee_sign_date || new Date().toISOString().split('T')[0]) : null,
        manager_id: formData.manager_id || null,
        manager_sign_date: formData.manager_signature ? (formData.manager_sign_date || new Date().toISOString().split('T')[0]) : null,
        witness_sign_date: formData.witness_signature ? (formData.witness_sign_date || new Date().toISOString().split('T')[0]) : null
      };
      
      delete apiData.points_assessed;
      
      const response = await makeApiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiData)
      });
      
      if (response.ok) {
        setSuccess(editingWarning ? 'Warning updated successfully' : 'Warning created successfully');
        setOpenDialog(false);
        loadWarnings();
      } else {
        // Try to get error message from response
        let errorMessage = 'Failed to save warning';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          if (errorData.details) {
            errorMessage += `: ${JSON.stringify(errorData.details)}`;
          }
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = `Failed to save warning: ${response.statusText || response.status}`;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      setWarningFormError(err.message || t('discipline.saveFailed'));
      console.error('Error saving warning:', err);
    }
  };

  const renderHeader = () => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Typography variant="h4" component="h1" color="primary" fontWeight="bold">
        Employee Discipline
      </Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, newView) => newView && setView(newView)}
          size="small"
        >
          <ToggleButton value="list">Warnings</ToggleButton>
          <ToggleButton value="points">Points System</ToggleButton>
          <ToggleButton value="analytics">Analytics</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateWarning}
        >
          Issue Warning
        </Button>
      </Box>
    </Box>
  );

  const renderStats = () => {
    // TKT-253: counters need to mirror the visible list filter so the
    // cards line up with the rows below. Compute filteredWarnings here
    // and derive displayStats from it instead of reading the global
    // /stats endpoint (which counted every row regardless of filter).
    // renderWarningsList recomputes the same filter independently;
    // both consume the same `warnings` + `filters` state so they stay
    // consistent without extra plumbing.
    const filteredForStats = warnings.filter(warning => {
      if (filters.employee !== 'all' && warning.employee_id !== parseInt(filters.employee)) return false;
      if (filters.status !== 'all' && warning.status !== filters.status) return false;
      if (filters.offense_type !== 'all' && warning.offense_type_id !== parseInt(filters.offense_type)) return false;
      return true;
    });
    const displayStats = {
      total_warnings: filteredForStats.length,
      active_warnings: filteredForStats.filter(w => (w.status || 'active') === 'active').length,
      resolved_warnings: filteredForStats.filter(w => w.status === 'resolved').length,
      total_points: filteredForStats.reduce(
        (sum, w) => sum + Number(w.points_assessed || w.points || 0),
        0
      ),
    };
    return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {displayStats.total_warnings}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Warnings
                </Typography>
              </Box>
              <GavelIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {displayStats.active_warnings}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Active Warnings
                </Typography>
              </Box>
              <WarningIcon color="error" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {displayStats.resolved_warnings}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Resolved
                </Typography>
              </Box>
              <CheckIcon color="success" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {displayStats.total_points}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Points
                </Typography>
              </Box>
              <TrendingUpIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    );
  };

  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={filters.employee}
                onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                label="Employee"
              >
                <MenuItem value="all">All Employees</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Offense Type</InputLabel>
              <Select
                value={filters.offense_type}
                onChange={(e) => setFilters({ ...filters, offense_type: e.target.value })}
                label="Offense Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {offenseTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.offense_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderWarningsList = () => {
    const filteredWarnings = warnings.filter(warning => {
      if (filters.employee !== 'all' && warning.employee_id !== parseInt(filters.employee)) return false;
      if (filters.status !== 'all' && warning.status !== filters.status) return false;
      if (filters.offense_type !== 'all' && warning.offense_type_id !== parseInt(filters.offense_type)) return false;
      return true;
    });

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Disciplinary Warnings
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Offense</TableCell>
                  <TableCell>Warning Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWarnings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((warning) => {
                    const offenseType = offenseTypes.find(t => t.id === warning.offense_type_id);
                    return (
                      <TableRow key={warning.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {warning.employee_name.charAt(0)}
                            </Avatar>
                            {warning.employee_name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getSeverityIcon(offenseType?.severity_level || 1)}
                            <Typography variant="body2">
                              {warning.offense_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={warning.type_of_warning}
                            color={getWarningTypeColor(warning.type_of_warning)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatScheduledDate(warning.warning_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={warning.points_assessed}
                            color={warning.points_assessed > 2 ? 'error' : warning.points_assessed > 1 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={warning.status.toUpperCase()}
                            color={getStatusColor(warning.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {formatScheduledDate(warning.expiration_date) || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewWarning(warning)}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditWarning(warning)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredWarnings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>
    );
  };

  // FB-065: Points System view
  const renderPointsView = () => {
    const totalAwarded = Number(pointsSummary?.total_awarded || 0);
    const totalDeducted = Number(pointsSummary?.total_deducted || 0);
    const netPoints = Number(pointsSummary?.net_points || 0);
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
            <Typography variant="h6">Employee Points System</Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="points-employee-label">Employee</InputLabel>
                <Select
                  labelId="points-employee-label"
                  value={pointsEmployeeFilter}
                  onChange={(e) => setPointsEmployeeFilter(e.target.value)}
                  label="Employee"
                  inputProps={{ 'aria-label': 'Filter points by employee' }}
                >
                  <MenuItem value="all">Choose an employee...</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.employee_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* TKT-269: the tab had no way to create a points entry. */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenPointsDialog}
                aria-label="Add a points entry for an employee"
              >
                Add Points Entry
              </Button>
            </Box>
          </Box>

          {pointsEmployeeFilter === 'all' ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Select an employee above to view their points history.
            </Alert>
          ) : pointsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="textSecondary">Total Awarded</Typography>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        +{totalAwarded}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="textSecondary">Total Deducted</Typography>
                      <Typography variant="h4" color="error.main" fontWeight="bold">
                        -{totalDeducted}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="textSecondary">Net Balance</Typography>
                      <Typography
                        variant="h4"
                        color={netPoints >= 0 ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {netPoints >= 0 ? '+' : ''}{netPoints}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" gutterBottom>Points History</Typography>
              {pointsData.length === 0 ? (
                <Alert severity="info">No points entries yet for this employee.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Awarded</TableCell>
                        <TableCell align="right">Deducted</TableCell>
                        <TableCell>Conduct</TableCell>
                        <TableCell>Misconduct</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pointsData.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{formatScheduledDate(row.point_date)}</TableCell>
                          <TableCell align="right">
                            {Number(row.points_awarded || 0) > 0 && (
                              <Chip
                                label={`+${row.points_awarded}`}
                                size="small"
                                color="success"
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.points_deducted || 0) > 0 && (
                              <Chip
                                label={`-${row.points_deducted}`}
                                size="small"
                                color="error"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.description_of_conduct || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.description_of_misconduct || '-'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  // FB-065: Analytics view
  const renderAnalyticsView = () => {
    if (analyticsLoading) {
      return (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      );
    }
    if (!analyticsData) {
      return (
        <Card>
          <CardContent>
            <Alert severity="warning">
              Unable to load discipline analytics. Make sure you have admin access and try again.
            </Alert>
            <Box mt={2}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAnalytics} aria-label="Retry loading analytics">
                Retry
              </Button>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const summary = analyticsData.summary || {};
    const byEmployee = analyticsData.by_employee || [];
    const byOffense = analyticsData.by_offense || [];
    const total = Number(summary.total_warnings || 0);
    const first = Number(summary.first_warnings || 0);
    const second = Number(summary.second_warnings || 0);
    const final = Number(summary.final_warnings || 0);
    const maxByOffense = byOffense.reduce((m, r) => Math.max(m, Number(r.count || 0)), 0) || 1;

    return (
      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Warning Distribution</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">Total Warnings</Typography>
                    <Typography variant="h4" fontWeight="bold">{total}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">Verbal / 1st</Typography>
                    <Typography variant="h4" color="info.main" fontWeight="bold">{first}</Typography>
                    <LinearProgress variant="determinate" value={total ? (first / total) * 100 : 0} sx={{ mt: 1 }} aria-label={`${first} of ${total} warnings`} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">Written / 2nd</Typography>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">{second}</Typography>
                    <LinearProgress variant="determinate" value={total ? (second / total) * 100 : 0} color="warning" sx={{ mt: 1 }} aria-label={`${second} of ${total} warnings`} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary">Final</Typography>
                    <Typography variant="h4" color="error.main" fontWeight="bold">{final}</Typography>
                    <LinearProgress variant="determinate" value={total ? (final / total) * 100 : 0} color="error" sx={{ mt: 1 }} aria-label={`${final} of ${total} warnings`} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Warnings by Employee</Typography>
            {byEmployee.length === 0 ? (
              <Alert severity="info">No employee warning data yet.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell align="right">Warnings</TableCell>
                      <TableCell align="right">Final Warnings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byEmployee.map((row, idx) => (
                      <TableRow key={`${row.employee_name}-${idx}`}>
                        <TableCell>{row.employee_name}</TableCell>
                        <TableCell>{row.department_name || '-'}</TableCell>
                        <TableCell align="right">
                          <Chip label={row.warning_count} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          {Number(row.final_warnings || 0) > 0 ? (
                            <Chip label={row.final_warnings} size="small" color="error" />
                          ) : (
                            <Typography variant="body2" color="textSecondary">0</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Warnings by Offense Type</Typography>
            {byOffense.length === 0 ? (
              <Alert severity="info">No offense breakdown available yet.</Alert>
            ) : (
              <Stack spacing={1}>
                {byOffense.map((row, idx) => (
                  <Box key={`${row.offense_name}-${idx}`}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        {getSeverityIcon(row.severity_level)}
                        <Typography variant="body2">{row.offense_name}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">{row.count}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(Number(row.count || 0) / maxByOffense) * 100}
                      color={getSeverityColor(row.severity_level)}
                      sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                      aria-label={`${row.count} ${row.offense_name} warnings`}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>
    );
  };

  // TKT-462: Employee Warning Notice PDF on company letterhead.
  const handleDownloadPdf = async (warning) => {
    setDownloadingPdf(true);
    setError('');
    try {
      const response = await makeApiCall(`api/discipline/warnings/${warning.id}/pdf`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('discipline.pdfFailed'));
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const name = String(warning.employee_name || 'employee').replace(/[^A-Za-z0-9]+/g, '-');
      link.download = `Warning-Notice-${name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading warning PDF:', err);
      setError(err.message || t('discipline.pdfFailed'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  // One signature block (employee, manager or witness). A saved signature
  // is shown as an image with a "Sign again" button instead of a blank pad.
  const renderSignatureField = (signer) => {
    const sigKey = `${signer}_signature`;
    const dateKey = `${signer}_sign_date`;
    const signature = formData[sigKey];
    return (
      <Grid size={12} key={signer}>
        <Divider sx={{ my: 1 }}>
          <Chip label={t(`discipline.signatures.${signer}.title`)} size="small" />
        </Divider>
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
          {t(`discipline.signatures.${signer}.help`)}
        </Typography>
        {signature ? (
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Box
              component="img"
              src={signature}
              alt={t(`discipline.signatures.${signer}.title`)}
              sx={{ maxHeight: 70, maxWidth: 280, border: '1px solid', borderColor: 'divider', p: 0.5, bgcolor: '#fff' }}
            />
            <Button
              size="small"
              onClick={() => setFormData((prev) => ({ ...prev, [sigKey]: null, [dateKey]: '' }))}
            >
              {t('discipline.signatures.signAgain')}
            </Button>
          </Stack>
        ) : (
          <SignaturePad
            label={t(`discipline.signatures.${signer}.title`)}
            onSignature={(dataUrl) => {
              setFormData((prev) => ({
                ...prev,
                [sigKey]: dataUrl || null,
                [dateKey]: dataUrl ? (prev[dateKey] || new Date().toISOString().split('T')[0]) : ''
              }));
            }}
          />
        )}
        {signature && (
          <TextField
            fullWidth
            label={t('discipline.signatures.signedOn')}
            type="date"
            size="small"
            value={formData[dateKey]}
            onChange={(e) => setFormData((prev) => ({ ...prev, [dateKey]: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }}
            inputProps={{ 'aria-label': t(`discipline.signatures.${signer}.dateAria`) }}
          />
        )}
      </Grid>
    );
  };

  const renderViewDialog = () => {
    if (!selectedWarning) return null;
    
    return (
      <Dialog open={!!selectedWarning} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Warning Details
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Employee</Typography>
              <Typography variant="body1">{selectedWarning.employee_name || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Warning Date</Typography>
              <Typography variant="body1">{selectedWarning.warning_date || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">{t('discipline.manager')}</Typography>
              <Typography variant="body1">{selectedWarning.manager_name || t('discipline.noManager')}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Warning Type</Typography>
              <Typography variant="body1">{convertWarningTypeToString(selectedWarning.type_of_warning) || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Typography variant="body1">{selectedWarning.status || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Offense Type</Typography>
              <Typography variant="body1">
                {offenseTypes.find(t => t.id === selectedWarning.offense_type_id)?.offense_name || 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Expiration Date</Typography>
              <Typography variant="body1">{selectedWarning.expiration_date || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Follow-up Date</Typography>
              <Typography variant="body1">{selectedWarning.follow_up_date || 'N/A'}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary">Description</Typography>
              <Typography variant="body1">{selectedWarning.description_of_infraction || 'N/A'}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary">Improvement Plan</Typography>
              <Typography variant="body1">{selectedWarning.improvement_plan || 'N/A'}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary">Consequences</Typography>
              <Typography variant="body1">{selectedWarning.consequence_of_infraction || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Employee Signature Date</Typography>
              <Typography variant="body1">{selectedWarning.employee_sign_date || 'Not signed'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Manager Signature Date</Typography>
              <Typography variant="body1">{selectedWarning.manager_sign_date || 'Not signed'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">Witness Signature Date</Typography>
              <Typography variant="body1">{selectedWarning.witness_sign_date || 'Not signed'}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          <Button
            variant="outlined"
            startIcon={downloadingPdf ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={() => handleDownloadPdf(selectedWarning)}
            disabled={downloadingPdf}
          >
            {t('discipline.downloadPdf')}
          </Button>
          <Button variant="contained" onClick={() => {
            handleCloseViewDialog();
            handleEditWarning(selectedWarning);
          }}>
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderWarningDialog = () => (
    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingWarning ? 'Edit Warning' : 'Issue New Warning'}
      </DialogTitle>
      <DialogContent>
        {warningFormError && (
          <Alert severity="error" onClose={() => setWarningFormError('')} sx={{ mt: 1, mb: 1 }} role="alert">
            {warningFormError}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                label="Employee"
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Offense Type</InputLabel>
              <Select
                value={formData.offense_type_id}
                onChange={(e) => {
                  const offenseType = offenseTypes.find(t => t.id === e.target.value);
                  setFormData({ 
                    ...formData, 
                    offense_type_id: e.target.value,
                    points_assessed: offenseType?.severity_level || 0
                  });
                }}
                label="Offense Type"
              >
                {offenseTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.offense_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('discipline.manager')}</InputLabel>
              <Select
                value={formData.manager_id}
                onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                label={t('discipline.manager')}
              >
                <MenuItem value="">{t('discipline.noManager')}</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.employee_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Warning Date"
              type="date"
              value={formData.warning_date}
              onChange={(e) => setFormData({ ...formData, warning_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Warning Type</InputLabel>
              <Select
                value={formData.type_of_warning}
                onChange={(e) => setFormData({ ...formData, type_of_warning: e.target.value })}
                label="Warning Type"
              >
                <MenuItem value="Verbal Warning">Verbal Warning</MenuItem>
                <MenuItem value="Written Warning">Written Warning</MenuItem>
                <MenuItem value="Final Warning">Final Warning</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Points Assessed"
              type="number"
              value={formData.points_assessed}
              onChange={(e) => setFormData({ ...formData, points_assessed: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
              inputProps={{ min: 0, max: 5 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Expiration Date"
              type="date"
              value={formData.expiration_date}
              onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Follow-up Date"
              type="date"
              value={formData.follow_up_date}
              onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Description of Infraction"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the incident and circumstances..."
              inputProps={{ 'aria-label': 'Description of infraction' }}
            />
          </Grid>
          {/* FB-064: Improvement Plan + Consequences fields */}
          <Grid size={12}>
            <TextField
              fullWidth
              label="Improvement Plan"
              multiline
              rows={3}
              value={formData.improvement_plan}
              onChange={(e) => setFormData({ ...formData, improvement_plan: e.target.value })}
              placeholder="What steps will the employee take to improve? Include specific, measurable actions and timelines..."
              inputProps={{ 'aria-label': 'Improvement plan' }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Consequences of Further Infractions"
              multiline
              rows={3}
              value={formData.consequence_of_infraction}
              onChange={(e) => setFormData({ ...formData, consequence_of_infraction: e.target.value })}
              placeholder="What will happen if this behaviour continues? (e.g. next step in progressive discipline)..."
              inputProps={{ 'aria-label': 'Consequences of further infractions' }}
            />
          </Grid>
          {/* FB-063 / TKT-462: employee, manager and witness signatures */}
          {SIGNERS.map(renderSignatureField)}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)} aria-label="Cancel warning">Cancel</Button>
        <Button variant="contained" onClick={handleSaveWarning} aria-label={editingWarning ? 'Update warning' : 'Issue warning'}>
          {editingWarning ? 'Update' : 'Issue Warning'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // TKT-269: create a points entry (awarded or deducted) for an employee.
  const renderPointsDialog = () => {
    const awarding = pointsForm.entry_type === 'awarded';
    return (
      <Dialog
        open={pointsDialogOpen}
        onClose={() => !pointsSaving && setPointsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Points Entry</DialogTitle>
        <DialogContent>
          {pointsFormError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} role="alert">
              {pointsFormError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel id="points-entry-employee-label">Employee</InputLabel>
                <Select
                  labelId="points-entry-employee-label"
                  value={pointsForm.employee_id}
                  label="Employee"
                  onChange={(e) => setPointsForm({ ...pointsForm, employee_id: e.target.value })}
                  inputProps={{ 'aria-label': 'Employee receiving the points entry' }}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.employee_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel id="points-entry-type-label">Entry Type</InputLabel>
                <Select
                  labelId="points-entry-type-label"
                  value={pointsForm.entry_type}
                  label="Entry Type"
                  onChange={(e) => setPointsForm({ ...pointsForm, entry_type: e.target.value })}
                  inputProps={{ 'aria-label': 'Whether points are awarded or deducted' }}
                >
                  <MenuItem value="awarded">Award Points</MenuItem>
                  <MenuItem value="deducted">Deduct Points</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                type="number"
                label="Points"
                value={pointsForm.points}
                onChange={(e) => setPointsForm({ ...pointsForm, points: e.target.value })}
                inputProps={{ min: 1, step: 1, 'aria-label': 'Number of points' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={awarding ? 'Description of Conduct' : 'Description of Misconduct'}
                value={pointsForm.description}
                onChange={(e) => setPointsForm({ ...pointsForm, description: e.target.value })}
                helperText={awarding
                  ? 'What the employee did to earn these points.'
                  : 'What the points are being deducted for.'}
                inputProps={{ 'aria-label': awarding ? 'Description of conduct' : 'Description of misconduct' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPointsDialogOpen(false)} disabled={pointsSaving} aria-label="Cancel points entry">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePoints}
            disabled={pointsSaving}
            startIcon={pointsSaving ? <CircularProgress size={18} color="inherit" /> : null}
            aria-label="Save points entry"
          >
            {pointsSaving ? 'Saving...' : 'Save Entry'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {renderHeader()}
      {renderStats()}
      {view === 'list' && renderFilters()}
      {view === 'list' && renderWarningsList()}
      {view === 'points' && renderPointsView()}
      {view === 'analytics' && renderAnalyticsView()}
      {renderWarningDialog()}
      {renderViewDialog()}
      {renderPointsDialog()}
    </Box>
  );
};

export default EmployeeDiscipline;
